import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { createListing, uploadListingPhoto, uploadListingVideo } from "../lib/api";
import Navbar from "../components/Navbar";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Switch } from "../components/ui/switch";
import { ArrowLeft, Upload, Plus, X, Video, Camera, Check, Loader2 } from "lucide-react";

export default function CreateListing() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [tourVideo, setTourVideo] = useState(null);
  const [tourVideoPreview, setTourVideoPreview] = useState(null);
  const [form, setForm] = useState({
    title: "", description: "", property_type: "apartment", price: "",
    location: "", city: "Virginia Beach", state: "VA",
    lease_type: "mid", bedrooms: 1, bathrooms: 1, sqft: "",
    amenities: [], pet_friendly: false, bah_eligible: false, available_date: "",
  });
  const [amenityInput, setAmenityInput] = useState("");

  const handleChange = (key, value) => setForm(prev => ({ ...prev, [key]: value }));

  const addAmenity = () => {
    if (amenityInput.trim() && !form.amenities.includes(amenityInput.trim())) {
      setForm(prev => ({ ...prev, amenities: [...prev.amenities, amenityInput.trim()] }));
      setAmenityInput("");
    }
  };

  const removeAmenity = (a) => setForm(prev => ({ ...prev, amenities: prev.amenities.filter(x => x !== a) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const res = await createListing({
        ...form,
        price: parseFloat(form.price),
        sqft: form.sqft ? parseInt(form.sqft) : null,
        bedrooms: parseInt(form.bedrooms),
        bathrooms: parseInt(form.bathrooms),
      });
      // Upload photos
      for (const photo of photos) {
        await uploadListingPhoto(res.data.listing_id, photo);
      }
      // Upload tour video
      if (tourVideo) {
        await uploadListingVideo(res.data.listing_id, tourVideo);
      }
      toast.success("Listing submitted! It will go live once our team approves it.");
      navigate("/landlord-dashboard");
    } catch (err) {
      console.error(err);
      toast.error(err?.response?.data?.detail || "Upload failed. Please try again.");
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: "#faf8f3" }}>
      <Navbar />
      <div className="max-w-3xl mx-auto px-6 md:px-12 py-8">
        <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-[#5a5a5a] hover:text-[#0d0d0d] mb-6" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" /> Back
        </button>

        <h1 className="text-2xl sm:text-3xl font-bold text-[#0d0d0d] font-['Fraunces'] mb-8" data-testid="create-listing-heading">
          Create New Listing
        </h1>

        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 space-y-4">
            <h2 className="font-semibold text-[#0d0d0d] font-['Fraunces']">Basic Info</h2>
            <div>
              <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Title</label>
              <Input data-testid="listing-title-input" value={form.title} onChange={(e) => handleChange("title", e.target.value)} placeholder="e.g. Cozy 2BR Apartment Near Beach" className="rounded-xl border-[#d5d3ce]" required />
            </div>
            <div>
              <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Description</label>
              <Textarea data-testid="listing-desc-input" value={form.description} onChange={(e) => handleChange("description", e.target.value)} placeholder="Describe the property..." className="rounded-xl border-[#d5d3ce] min-h-[120px]" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Property Type</label>
                <Select value={form.property_type} onValueChange={(v) => handleChange("property_type", v)}>
                  <SelectTrigger data-testid="listing-type-select" className="rounded-xl border-[#d5d3ce]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="room">Room</SelectItem>
                    <SelectItem value="apartment">Apartment</SelectItem>
                    <SelectItem value="house">House</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Monthly Price ($)</label>
                <Input data-testid="listing-price-input" type="number" min="1" value={form.price} onChange={(e) => handleChange("price", e.target.value)} placeholder="1200" className="rounded-xl border-[#d5d3ce]" required />
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 space-y-4">
            <h2 className="font-semibold text-[#0d0d0d] font-['Fraunces']">Location</h2>
            <div>
              <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Address</label>
              <Input data-testid="listing-address-input" value={form.location} onChange={(e) => handleChange("location", e.target.value)} placeholder="123 Main St" className="rounded-xl border-[#d5d3ce]" required />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">City</label>
                <Select value={form.city} onValueChange={(v) => handleChange("city", v)}>
                  <SelectTrigger className="rounded-xl border-[#d5d3ce]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Norfolk">Norfolk, VA</SelectItem>
                    <SelectItem value="Virginia Beach">Virginia Beach, VA</SelectItem>
                    <SelectItem value="Suffolk">Suffolk, VA</SelectItem>
                    <SelectItem value="Chesapeake">Chesapeake, VA</SelectItem>
                    <SelectItem value="Newport News">Newport News, VA</SelectItem>
                    <SelectItem value="Hampton">Hampton, VA</SelectItem>
                    <SelectItem value="Portsmouth">Portsmouth, VA</SelectItem>
                    <SelectItem value="Williamsburg">Williamsburg, VA</SelectItem>
                    <SelectItem value="Yorktown">Yorktown, VA</SelectItem>
                    <SelectItem value="Arlington">Arlington, VA</SelectItem>
                    <SelectItem value="Alexandria">Alexandria, VA</SelectItem>
                    <SelectItem value="Falls Church">Falls Church, VA</SelectItem>
                    <SelectItem value="Springfield">Springfield, VA</SelectItem>
                    <SelectItem value="West Springfield">West Springfield, VA</SelectItem>
                    <SelectItem value="Fairfax">Fairfax, VA</SelectItem>
                    <SelectItem value="McLean">McLean, VA</SelectItem>
                    <SelectItem value="Reston">Reston, VA</SelectItem>
                    <SelectItem value="Herndon">Herndon, VA</SelectItem>
                    <SelectItem value="Vienna">Vienna, VA</SelectItem>
                    <SelectItem value="Tysons">Tysons, VA</SelectItem>
                    <SelectItem value="Centreville">Centreville, VA</SelectItem>
                    <SelectItem value="Chantilly">Chantilly, VA</SelectItem>
                    <SelectItem value="Manassas">Manassas, VA</SelectItem>
                    <SelectItem value="Woodbridge">Woodbridge, VA</SelectItem>
                    <SelectItem value="Dumfries">Dumfries, VA</SelectItem>
                    <SelectItem value="Dale City">Dale City, VA</SelectItem>
                    <SelectItem value="Quantico">Quantico, VA</SelectItem>
                    <SelectItem value="Fort Belvoir">Fort Belvoir, VA</SelectItem>
                    <SelectItem value="Stafford">Stafford, VA</SelectItem>
                    <SelectItem value="Fredericksburg">Fredericksburg, VA</SelectItem>
                    <SelectItem value="Spotsylvania">Spotsylvania, VA</SelectItem>
                    <SelectItem value="Annandale">Annandale, VA</SelectItem>
                    <SelectItem value="Burke">Burke, VA</SelectItem>
                    <SelectItem value="Lorton">Lorton, VA</SelectItem>
                    <SelectItem value="Mount Vernon">Mount Vernon, VA</SelectItem>
                    <SelectItem value="Great Falls">Great Falls, VA</SelectItem>
                    <SelectItem value="Oakton">Oakton, VA</SelectItem>
                    <SelectItem value="Merrifield">Merrifield, VA</SelectItem>
                    <SelectItem value="Gainesville">Gainesville, VA</SelectItem>
                    <SelectItem value="Haymarket">Haymarket, VA</SelectItem>
                    <SelectItem value="Bristow">Bristow, VA</SelectItem>
                    <SelectItem value="Ashburn">Ashburn, VA</SelectItem>
                    <SelectItem value="Leesburg">Leesburg, VA</SelectItem>
                    <SelectItem value="Sterling">Sterling, VA</SelectItem>
                    <SelectItem value="Purcellville">Purcellville, VA</SelectItem>
                    <SelectItem value="Warrenton">Warrenton, VA</SelectItem>
                    <SelectItem value="Culpeper">Culpeper, VA</SelectItem>
                    <SelectItem value="Front Royal">Front Royal, VA</SelectItem>
                    <SelectItem value="Winchester">Winchester, VA</SelectItem>
                    <SelectItem value="Washington DC">Washington, DC</SelectItem>
                    <SelectItem value="Bethesda">Bethesda, MD</SelectItem>
                    <SelectItem value="Silver Spring">Silver Spring, MD</SelectItem>
                    <SelectItem value="Rockville">Rockville, MD</SelectItem>
                    <SelectItem value="Gaithersburg">Gaithersburg, MD</SelectItem>
                    <SelectItem value="Germantown">Germantown, MD</SelectItem>
                    <SelectItem value="College Park">College Park, MD</SelectItem>
                    <SelectItem value="Bowie">Bowie, MD</SelectItem>
                    <SelectItem value="Largo">Largo, MD</SelectItem>
                    <SelectItem value="Laurel">Laurel, MD</SelectItem>
                    <SelectItem value="Hyattsville">Hyattsville, MD</SelectItem>
                    <SelectItem value="Greenbelt">Greenbelt, MD</SelectItem>
                    <SelectItem value="Takoma Park">Takoma Park, MD</SelectItem>
                    <SelectItem value="Frederick">Frederick, MD</SelectItem>
                    <SelectItem value="Waldorf">Waldorf, MD</SelectItem>
                    <SelectItem value="Capitol Heights">Capitol Heights, MD</SelectItem>
                    <SelectItem value="New Carrollton">New Carrollton, MD</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Lease Type</label>
                <Select value={form.lease_type} onValueChange={(v) => handleChange("lease_type", v)}>
                  <SelectTrigger className="rounded-xl border-[#d5d3ce]"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="short">Short-Term (30-90d)</SelectItem>
                    <SelectItem value="mid">Mid-Term (3-12mo)</SelectItem>
                    <SelectItem value="long">Long-Term (1yr+)</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 space-y-4">
            <h2 className="font-semibold text-[#0d0d0d] font-['Fraunces']">Details</h2>
            <div className="grid grid-cols-3 gap-4">
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Bedrooms</label>
                <Input data-testid="listing-beds-input" type="number" min="1" value={form.bedrooms} onChange={(e) => handleChange("bedrooms", e.target.value)} className="rounded-xl border-[#d5d3ce]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Bathrooms</label>
                <Input type="number" min="1" value={form.bathrooms} onChange={(e) => handleChange("bathrooms", e.target.value)} className="rounded-xl border-[#d5d3ce]" />
              </div>
              <div>
                <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Sqft</label>
                <Input type="number" value={form.sqft} onChange={(e) => handleChange("sqft", e.target.value)} placeholder="Optional" className="rounded-xl border-[#d5d3ce]" />
              </div>
            </div>
            <div>
              <label className="text-sm font-medium text-[#0d0d0d] mb-1 block">Available Date</label>
              <Input type="date" value={form.available_date} onChange={(e) => handleChange("available_date", e.target.value)} className="rounded-xl border-[#d5d3ce]" data-testid="listing-date-input" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-[#0d0d0d]">Pet Friendly</label>
              <Switch checked={form.pet_friendly} onCheckedChange={(v) => handleChange("pet_friendly", v)} data-testid="listing-pet-switch" />
            </div>
            <div className="flex items-center justify-between">
              <label className="text-sm font-medium text-[#0d0d0d]">BAH Eligible</label>
              <Switch checked={form.bah_eligible} onCheckedChange={(v) => handleChange("bah_eligible", v)} data-testid="listing-bah-switch" />
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 space-y-4">
            <h2 className="font-semibold text-[#0d0d0d] font-['Fraunces']">Amenities</h2>
            <div className="flex gap-2">
              <Input value={amenityInput} onChange={(e) => setAmenityInput(e.target.value)} placeholder="e.g. WiFi, Pool, Gym..." className="rounded-xl border-[#d5d3ce]"
                onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addAmenity(); } }} />
              <Button type="button" onClick={addAmenity} variant="outline" className="rounded-xl border-[#d5d3ce]"><Plus className="w-4 h-4" /></Button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.amenities.map(a => (
                <span key={a} className="bg-[#faf8f3] text-[#0d0d0d] rounded-full px-3 py-1 text-sm flex items-center gap-1">
                  {a} <button type="button" onClick={() => removeAmenity(a)}><X className="w-3 h-3" /></button>
                </span>
              ))}
            </div>
          </div>

          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 space-y-4">
            <h2 className="font-semibold text-[#0d0d0d] font-['Fraunces']">Photos</h2>
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 cursor-pointer bg-[#faf8f3] rounded-xl px-4 py-3 border border-dashed border-[#d5d3ce] hover:border-[#1a6b3c] transition-colors" data-testid="photo-upload-label">
                <Upload className="w-5 h-5 text-[#5a5a5a]" />
                <span className="text-sm text-[#5a5a5a]">Upload Photos</span>
                <input type="file" accept="image/*" multiple className="hidden" onChange={(e) => setPhotos(prev => [...prev, ...Array.from(e.target.files)])} />
              </label>
              <span className="text-sm text-[#5a5a5a]">{photos.length} selected</span>
            </div>
            {photos.length > 0 && (
              <div className="flex gap-2 flex-wrap">
                {photos.map((p, i) => (
                  <div key={i} className="relative">
                    <img src={URL.createObjectURL(p)} alt="" className="w-20 h-20 rounded-xl object-cover" />
                    <button type="button" onClick={() => setPhotos(prev => prev.filter((_, idx) => idx !== i))}
                      className="absolute -top-2 -right-2 w-5 h-5 bg-red-500 text-white rounded-full flex items-center justify-center text-xs">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Property Tour Video */}
          <div className="bg-white rounded-2xl p-6 border border-[#d5d3ce]/60 space-y-4" data-testid="tour-video-section">
            <h2 className="font-semibold text-[#0d0d0d] font-['Fraunces']">Property Tour Video</h2>
            <p className="text-xs text-[#888]">Upload a walkthrough video of your property. Show the full layout, key features, and amenities.</p>
            
            <div className="flex flex-wrap gap-1.5 mb-3">
              {["Full layout walkthrough", "Rooms & kitchen", "Bathroom & storage", "Key features & amenities", "Outdoor/parking areas"].map((p) => (
                <span key={p} className="bg-[#faf8f3] text-[#555] text-[11px] px-2.5 py-1 rounded-full border border-black/[0.04]">{p}</span>
              ))}
            </div>

            {tourVideoPreview ? (
              <div className="space-y-3">
                <div className="relative rounded-xl overflow-hidden bg-black">
                  <video src={tourVideoPreview} controls className="w-full max-h-[200px] object-contain" data-testid="tour-video-preview" />
                  <button type="button" onClick={() => { setTourVideo(null); setTourVideoPreview(null); }}
                    className="absolute top-2 right-2 w-7 h-7 bg-black/60 text-white rounded-full flex items-center justify-center hover:bg-black/80">
                    <X className="w-4 h-4" />
                  </button>
                </div>
                <div className="flex items-center gap-2 text-xs text-[#1a6b3c] font-medium">
                  <Check className="w-3.5 h-3.5" /> {tourVideo?.name} — ready to upload
                </div>
              </div>
            ) : (
              <div className="grid grid-cols-2 gap-2">
                <label className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-[#d5d3ce] hover:border-[#1a6b3c] transition-colors cursor-pointer" data-testid="tour-video-upload-btn">
                  <Upload className="w-5 h-5 text-[#888]" />
                  <span className="text-xs font-medium text-[#888]">Upload File</span>
                  <span className="text-[10px] text-[#aaa]">MP4, MOV · 100MB max</span>
                  <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov" className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) { setTourVideo(f); setTourVideoPreview(URL.createObjectURL(f)); }
                    }}
                  />
                </label>
                <label className="flex flex-col items-center gap-2 p-4 rounded-xl border-2 border-dashed border-[#d5d3ce] hover:border-[#1a6b3c] transition-colors cursor-pointer" data-testid="tour-video-record-btn">
                  <Camera className="w-5 h-5 text-[#888]" />
                  <span className="text-xs font-medium text-[#888]">Record</span>
                  <span className="text-[10px] text-[#aaa]">Use camera</span>
                  <input type="file" accept="video/mp4,video/quicktime,.mp4,.mov" capture="environment" className="hidden"
                    onChange={(e) => {
                      const f = e.target.files?.[0];
                      if (f) { setTourVideo(f); setTourVideoPreview(URL.createObjectURL(f)); }
                    }}
                  />
                </label>
              </div>
            )}
          </div>

          <Button data-testid="submit-listing-btn" type="submit" disabled={loading} className="w-full h-14 rounded-full bg-[#c8f563] text-[#0d0d0d] hover:bg-[#b8e553] text-lg font-semibold">
            {loading ? "Creating..." : "Publish Listing"}
          </Button>
        </form>
      </div>
    </div>
  );
}
